import { Expose, Type } from "class-transformer";
import { GeoJSONGeometry } from "ol/format/GeoJSON";
import { Steps, StepStatus } from "@/constants/analysis-status/index";
import { IFilter } from "../types";
import { formatNumber } from "@/utils/numbers.ts";
import { GaiiaArch } from "@/constants/gaiia-arch";
export class Analysis {
  public uuid!: string;
  
  @Type(() => Date)
  @Expose({ name: "created_at" })
  public createdAt!: Date | null;
  
  public status!: string;

  public finished!: boolean;
  
  @Expose({ name: "summer_crop_type" })
  public summerCropType!: string;
  
  @Expose({ name: "winter_crop_type" })
  public winterCropType!: string;
  
  public approved!: boolean;
  
  @Expose({ name: "owner_uuid" })
  public ownerUuid: string = "";
  
  @Expose({ name: "owner_name" })
  public ownerName: string = "";
  
  @Expose({ name: "farm_uuid" })
  public farmUuid: string = "";
  
  @Expose({ name: "farm_name" })
  public farmName: string = "";
  
  @Expose({ name: "farm_area" })
  public farmArea: number = 0.0;
  
  @Expose({ name: "farm_geom" })
  public farmGeom!: GeoJSONGeometry;
  
  public city: string = "";
  public state: string = "";
  
  @Expose({ name: "micro_region" })
  public microRegion: string = "";
  
  @Type(() => FarmAnalysis)
  @Expose({ name: "farm_analyses" })
  public farmAnalyses: FarmAnalysis[] = [];

  @Expose({ name: "analysis_type"})
  public analysisType: string = "";

  @Expose({ name: "preferred_yieldest_source"})
  public preferredYieldestSource: string = "";

  @Expose({ name: "preferred_cropid_source"})
  public preferredCropIdSource: string = "";

  @Expose({ name: "unified" })
  public unified: boolean = false;

  get safePreferredYieldestSource() {
    return this.preferredYieldestSource == null ||  this.preferredYieldestSource == '' ? GaiiaArch.THEMIS : this.preferredYieldestSource;
  }

  get safePreferredCropIdSource() {
    return this.preferredCropIdSource == null ||  this.preferredCropIdSource == '' ? GaiiaArch.THEMIS : this.preferredCropIdSource;
  }
  
  get period() {
    return this.farmAnalyses.map(it => it.analysisYear).join("-");
  }

  get cropType() {
    return this.summerCropType ? this.summerCropType : this.winterCropType;
  }

  get harvestSeason() {
    return this.summerCropType ? 'SUMMER' : 'WINTER';
  }

  public isSentinela = this.isOfType('SENTINELA')
  public isAnalitica = this.isOfType('ANALITICA')

  private isOfType(analysisType: string) { return () => {return this.analysisType === analysisType }}
  
}

export class FarmAnalysis {
  public uuid!: string;
  
  @Expose({ name: "analysis_year" })
  public analysisYear!: number;
  
  @Expose({ name: "agriculture_mode" })
  public agricultureMode: string = "";
  
  @Expose({ name: "summer_crop_type" })
  public summerCropType!: string;
  
  @Expose({ name: "winter_crop_type" })
  public winterCropType!: string;
  
  @Expose({ name: "current_step" })
  public currentStep: string = "";
  
  @Expose({ name: "current_step_status" })
  public currentStepStatus: string = "";
  
  @Expose({ name: "current_step_message" })
  public currentStepMessage: string = "";

  @Expose({ name: "analysis_type"})
  public analysisType: string = "";

  public get completed() {
    return (
      this.currentStep === Steps.SUMMARY && this.currentStepStatus === StepStatus.FINISHED
    );
  }

  public get isProcessing() {
    return this.currentStepStatus === StepStatus.PROCESSING
  }
  
  public get cropType() {
    return this.summerCropType ? this.summerCropType : this.winterCropType;
  }

  public get isFinished() {
    return (this.currentStep === Steps.SUMMARY &&
      this.currentStepStatus === StepStatus.FINISHED) ||
      this.currentStepStatus === StepStatus.ERROR 
  }
}


export class AnalysisDTO {
  @Expose({ name: "farm_uuid" })
  public farmUuid!: string | null;

  @Expose({ name: "summer_crop_type" })
  public summerCropType!: string | null;

  @Expose({ name: "winter_crop_type" })
  public winterCropType!: string | null;

  public geom!: GeoJSONGeometry;

  @Expose({ name: "years" })
  public farmAnalysisYears!: number[];

  @Expose({ name: "monitoring_year" })
  public monitoringYear!: number;
}

export class AnalysisResponseDTO {
  public uuid!: string;

  public approved!: boolean;

  public status!: string;

  @Type(() => FarmAnalysis)
  @Expose({ name: "farm_analyses" })
  public farmAnalyses!: FarmAnalysis[];
}

export function analysisDTO(
  farmUuid: string | null,
  summerCropType: string | null,
  winterCropType: string | null,
  geom: GeoJSONGeometry,
  farmAnalysisYears: number[],
  monitoringYear: number
) {
  const analysisDTOAux = new AnalysisDTO();

  analysisDTOAux.farmUuid = farmUuid;
  analysisDTOAux.summerCropType = summerCropType;
  analysisDTOAux.winterCropType = winterCropType;
  analysisDTOAux.farmAnalysisYears = farmAnalysisYears;
  analysisDTOAux.geom = geom;
  analysisDTOAux.farmAnalysisYears = farmAnalysisYears;
  analysisDTOAux.monitoringYear = monitoringYear;

  return analysisDTOAux;
}

export class ComparativeScoreChartDTO {
  @Expose({ name: "analysis_year" })
  public analysisYear!: number;

  @Expose({ name: "yield_score_city" })
  public scoreCity!: number;

  @Expose({ name: "yield_score_country" })
  public scoreCountry!: number;

  @Expose({ name: "yield_score_micro_region" })
  public scoreMicroRegion!: number;

  @Expose({ name: "yield_score_state" })
  public scoreState!: number;
}
export class AreaExpansionChartDTO {
  @Expose({ name: "harvest_year" })
  public harvestYear!: number;

  @Expose({ name: "arable_area" })
  public arableArea!: number;

  @Expose({ name: "total_area" })
  public totalArea!: number;

  @Expose({ name: "crop_area" })
  public cropArea!: number;
}

export class YieldByYearDTO {
  @Expose({ name: "harvest_year" })
  public harvestYear!: number;

  @Expose({ name: "min_yield" })
  public minYield!: number;

  @Expose({ name: "max_yield" })
  public maxYield!: number;

  @Expose({ name: "lower_quartile" })
  public lowerQuartile!: number;

  @Expose({ name: "median" })
  public median!: number;

  @Expose({ name: "upper_quartile" })
  public upperQuartile!: number;

  get asArrayFormatted() {
    return [
      formatNumber(this.minYield), 
      formatNumber(this.lowerQuartile), 
      formatNumber(this.median), 
      formatNumber(this.upperQuartile),
      formatNumber(this.maxYield)
    ]
  }
}

export class ScoreAreaComparativeDTO {
  @Expose({ name: "analysis_year" })
  public analysisYear!: number;

  @Type(() => ScoreAreaDTO)
  @Expose({ name: "data" })
  public scoreAreaDTOS!: ScoreAreaDTO[];

  @Expose({ name: "range" })
  public range!: number;

}

export class ScoreAreaDTO {
  @Expose({ name: "score_range" })
  public scoreRange!: number;

  @Expose({ name: "area_percentage" })
  public areaPercentage!: number;
}

export const YIELD_SCORE_TYPE : string[] = [
  "YIELD_SCORE_CITY",
  "YIELD_SCORE_MICRO_REGION",
  "YIELD_SCORE_STATE",
  "YIELD_SCORE_COUNTRY"
];


export class AnalysisSummaryDTO {
  @Expose({ name: "crop_type" })
  public cropType!: string;

  @Type(() => Date)
  @Expose({ name: "average_planting_date" })
  public averageSeedingDate!: Date | null;

  @Type(() => Date)
  @Expose({ name: "average_previous_planting_date" })
  public averagePreviousSeedingDate!: Date | null;

  @Type(() => Date)
  @Expose({ name: "average_harvest_date" })
  public averageHarvestDate!: Date | null;

  @Type(() => Date)
  @Expose({ name: "average_previous_harvest_date" })
  public averagePreviousHarvestDate!: Date | null;
  public yield!: number;

  @Expose({ name: "crop_area"})
  public cropArea!: number;

  @Expose({ name: "harvest_season" })
  public harvestSeason!: string;

  @Expose({ name: "s3_geojson_url" })
  public s3GeoJsonUrl!: string;

  @Expose({ name: "arable_area" })
  public arableArea!: number;

  @Expose({ name: "total_area" })
  public totalArea!: number;

  @Expose({ name: "area_expansion" })
  public areaExpansion!: number;

  @Type(() => Date)
  @Expose({ name: "last_modified" })
  public lastModified!: Date | null;
}

export class AnalysisYieldEvolutionDTO {

  @Type(() => Date)
  @Expose({ name: "scene_date" })
  public sceneDate!: Date;

  public average!: number;

  @Expose({ name: 'area_sum' })
  public areaSum!: number;

   @Expose({ name: 'crop_count' })
  public cropCount!: number;

}

export class LastYearYieldDTO {
  public yield!: number;
}


export class CropsStagesDTO {
  public stage!: string;

  public percentage!: number;

  @Type(() => Date)
  @Expose({ name: "average_date" })
  public averageDate!: Date | null;

}

export class SummaryCropStagesDTO {
  @Type(() => Date)
  @Expose({ name: "average_date" })
  public averageDate!: Date | null;
  
  @Type(() => Date)
  @Expose({ name: "average_date_confirmed" })
  public averageDateConfirmed!: Date | null;

  @Type(() => Date)
  @Expose({ name: "average_date_not_confirmed" })
  public averageDateNotConfirmed!: Date | null;
  public stage!: string;
  public percentage!: string;

  @Expose({ name: "percentage_confirmed" })
  public percentageConfirmed!: string;

  @Expose({ name: "percentage_not_confirmed" })
  public percentageNotConfirmed!: string;
  
}

export class AnalysisImageAreaData {
  @Type(() => Date)
  @Expose({ name: 'scene_date'})
  public sceneDate!: Date;

  @Expose({ name: 'arable_area'})
  public arableArea!: number;
  
  @Expose({ name: 'crop_area_no_noise'})
  public cropAreaNoNoise!: number;
  
  @Expose({ name: 'crop_area'})
  public cropArea!: number;
  
  @Expose({ name: 'visible_percent'})
  public visiblePercent!: number;
}
export class AnalysesCount {
  public value!: number;
}
export class AnalysesMonitoringCount {
  public value!: number;
}

export class ProcessingCount {
  public processingTotal!: number;
}

export class AnalysesListRow {
  public uuid!: string;
  public year!: string;
  public analysisType!: string;
  public cropType!: string;
  public farmName!: string;
  public ownerName!: string;
  public stepStatus!: string;
  public stepMessage!: string;
  public step!: string;
  
  public msg!: string;

  get finishedWithSuccess(): boolean {
    return this.step === Steps.SUMMARY && this.stepStatus === StepStatus.FINISHED;
  }

  get finishedWithError(): boolean {
    return this.stepStatus === StepStatus.ERROR;
  }

  get processing(): boolean {
    return this.stepStatus === StepStatus.PROCESSING;
  }
}


export class AnalysesFilter implements IFilter {
  constructor(
    public companyId?: string,
    public farmName?: string,
    public ownerName?: string,
    public stepStatus?: string,
    public cropType?: string,
    public onlyUserOwners?: boolean,
  ) {}
}

export class FarmMapFull {
  public name!: string;
  public owner!: string;
  public city!: string;
  public state!: string;
  public area: number = 0.0;
  public geom!: GeoJSONGeometry;
}
